import React, { useReducer, useRef, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import { defaultIconState, defaultAppState, appSettings } from './apps';
import Modal from './Modal';
import Footer, { FooterApp } from './Footer';
import Windows from './Windows';
import Icons, { IconProps } from './Icons';
import { DashedBox } from 'components';
import backgroundImage from 'assets/bliss.jpeg';
import BlueScreen from './apps/BlueScreen/blue-screen';
import ReactDOM from 'react-dom';

interface Icon {
  id: number;
  isFocus: boolean;
}

interface App {
  id: number;
  zIndex: number;
  component: React.ElementType;
  minimized: boolean;
  maximized?: boolean;
  multiInstance?: boolean;
  injectProps?: Record<string, any>;
  header?: { icon: any; title: string };
  defaultSize?: { width: number; height: number };
  defaultOffset?: { x: number; y: number };
  resizable?: boolean;
}

interface State {
  apps: App[];
  nextAppID: number;
  nextZIndex: number;
  focusing: FOCUSING;
  icons: Icon[];
  selecting: { x: number; y: number } | null;
  powerState: POWER_STATE;
}

type Action =
  | { type: typeof ADD_APP; payload: App }
  | { type: typeof DEL_APP; payload: number }
  | { type: typeof FOCUS_APP; payload: number }
  | { type: typeof MINIMIZE_APP; payload: number }
  | { type: typeof TOGGLE_MAXIMIZE_APP; payload: number }
  | { type: typeof FOCUS_ICON; payload: number }
  | { type: typeof SELECT_ICONS; payload: number[] }
  | { type: typeof FOCUS_DESKTOP }
  | { type: typeof START_SELECT; payload: { x: number; y: number } }
  | { type: typeof END_SELECT }
  | { type: typeof POWER_OFF; payload: POWER_STATE }
  | { type: typeof CANCEL_POWER_OFF };

const initialState: State = {
  apps: defaultAppState,
  nextAppID: defaultAppState.length,
  nextZIndex: defaultAppState.length,
  focusing: FOCUSING.WINDOW,
  icons: defaultIconState,
  selecting: null,
  powerState: POWER_STATE.START,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface AppSetting {
  header: {
    icon: any;
    title: string;
  };
  component: React.ElementType;
  defaultSize: {
    width: number;
    height: number;
  };
  defaultOffset: {
    x: number;
    y: number;
  };
  resizable: boolean;
  minimized: boolean;
  maximized: boolean;
  multiInstance: boolean;
  injectProps?: {
    message?: string;
  };
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ADD_APP: {
      const existingApp = state.apps.find(
        app => app.component === action.payload.component,
      );
      if (action.payload.multiInstance || !existingApp) {
        const newApp: App = {
          ...action.payload,
          id: state.nextAppID,
          zIndex: state.nextZIndex,
          minimized: false,
        };
        return {
          ...state,
          apps: [...state.apps, newApp],
          nextAppID: state.nextAppID + 1,
          nextZIndex: state.nextZIndex + 1,
          focusing: FOCUSING.WINDOW,
        };
      }
      const updatedApps = state.apps.map(app =>
        app.component === action.payload.component
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps: updatedApps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case DEL_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const remainingApps = state.apps.filter(app => app.id !== action.payload);
      const newFocusing =
        remainingApps.length > 0
          ? FOCUSING.WINDOW
          : state.icons.some(icon => icon.isFocus)
          ? FOCUSING.ICON
          : FOCUSING.DESKTOP;
      return {
        ...state,
        apps: remainingApps,
        focusing: newFocusing,
      };
    }
    case FOCUS_APP: {
      const updatedApps = state.apps.map(app =>
        app.id === action.payload
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps: updatedApps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP: {
      const updatedApps = state.apps.map(app =>
        app.id === action.payload ? { ...app, minimized: true } : app,
      );
      return {
        ...state,
        apps: updatedApps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case TOGGLE_MAXIMIZE_APP: {
      const updatedApps = state.apps.map(app =>
        app.id === action.payload ? { ...app, maximized: !app.maximized } : app,
      );
      return {
        ...state,
        apps: updatedApps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case FOCUS_ICON: {
      const updatedIcons = state.icons.map(icon => ({
        ...icon,
        isFocus: icon.id === action.payload,
      }));
      return {
        ...state,
        icons: updatedIcons,
        focusing: FOCUSING.ICON,
      };
    }
    case SELECT_ICONS: {
      const updatedIcons = state.icons.map(icon => ({
        ...icon,
        isFocus: action.payload.includes(icon.id),
      }));
      return {
        ...state,
        icons: updatedIcons,
        focusing: FOCUSING.ICON,
      };
    }
    case FOCUS_DESKTOP: {
      const updatedIcons = state.icons.map(icon => ({
        ...icon,
        isFocus: false,
      }));
      return {
        ...state,
        icons: updatedIcons,
        focusing: FOCUSING.DESKTOP,
      };
    }
    case START_SELECT: {
      const updatedIcons = state.icons.map(icon => ({
        ...icon,
        isFocus: false,
      }));
      return {
        ...state,
        icons: updatedIcons,
        selecting: action.payload,
        focusing: FOCUSING.DESKTOP,
      };
    }
    case END_SELECT:
      return {
        ...state,
        selecting: null,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
};

const WinXP: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const ref = useRef<HTMLDivElement>(null);
  const mouse = useMouse(ref);

  const getFocusedAppId = useCallback((): number => {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .filter(app => !app.minimized)
      .sort((a, b) => b.zIndex - a.zIndex)[0];
    return focusedApp ? focusedApp.id : -1;
  }, [state.apps, state.focusing]);

  const focusedAppId = getFocusedAppId();

  const onFocusApp = useCallback(
    (id: number) => {
      dispatch({ type: FOCUS_APP, payload: id });
    },
    [dispatch],
  );

  const onMaximizeWindow = useCallback(
    (id: number) => {
      dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
    },
    [dispatch],
  );

  const onMinimizeWindow = useCallback(
    (id: number) => {
      dispatch({ type: MINIMIZE_APP, payload: id });
    },
    [dispatch],
  );

  const onCloseApp = useCallback(
    (id: number) => {
      dispatch({ type: DEL_APP, payload: id });
    },
    [dispatch],
  );

  const onMouseDownFooterApp = (id: string) => {
    const numericId = parseInt(id, 10);
    if (focusedAppId === numericId) {
      dispatch({ type: MINIMIZE_APP, payload: numericId });
    } else {
      dispatch({ type: FOCUS_APP, payload: numericId });
    }
  };

  const onMouseDownIcon = (id: number) => {
    dispatch({ type: FOCUS_ICON, payload: id });
  };

  const onDoubleClickIcon = (component: React.ElementType) => {
    const appSetting = Object.values(appSettings).find(
      setting => setting.component === component,
    );

    if (appSetting?.fullscreenBlueScreen) {
      // Render BlueScreen directly in full screen
      document.body.innerHTML = ''; // Clear everything
      const root = document.createElement('div');
      root.style.position = 'fixed';
      root.style.top = '0';
      root.style.left = '0';
      root.style.width = '100vw';
      root.style.height = '100vh';
      document.body.appendChild(root);

      // Render BlueScreen into the root
      ReactDOM.render(<BlueScreen />, root);
      return;
    }

    // Regular window handling
    if (appSetting) {
      const newApp: App = {
        ...appSetting,
        id: Date.now(),
        zIndex: 0,
      };
      dispatch({ type: ADD_APP, payload: newApp });
    }
  };

  const onMouseDownFooter = () => {
    dispatch({ type: FOCUS_DESKTOP });
  };

  const onClickMenuItem = (option: string) => {
    const appKey = option.replace(/\s+/g, '');
    const appSetting = appSettings[appKey];
    if (appSetting) {
      const newApp: App = {
        ...appSetting,
        id: Date.now(), // Generate a unique id
        zIndex: 0, // Set initial zIndex
      };
      dispatch({ type: ADD_APP, payload: newApp });
    } else if (option === 'Log Off') {
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    } else if (option === 'Turn Off Computer') {
      dispatch({ type: POWER_OFF, payload: POWER_STATE.TURN_OFF });
    } else {
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Error,
          id: Date.now(),
          zIndex: 0,
          injectProps: { message: 'C:\\\nApplication not found' },
        },
      });
    }
  };

  const onMouseDownDesktop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
    }
  };

  const onMouseUpDesktop = () => {
    dispatch({ type: END_SELECT });
  };

  const onIconsSelected = useCallback(
    (iconIds: number[]) => {
      dispatch({ type: SELECT_ICONS, payload: iconIds });
    },
    [dispatch],
  );

  const onClickModalButton = (text: string) => {
    dispatch({ type: CANCEL_POWER_OFF });
    const errorApp: App = {
      ...appSettings.Error,
      id: Date.now(), // Generate a unique id
      zIndex: 0, // Set initial zIndex
    };
    dispatch({
      type: ADD_APP,
      payload: errorApp,
    });
  };

  const onModalClose = () => {
    dispatch({ type: CANCEL_POWER_OFF });
  };

  const icons = state.icons
    .map(iconState => {
      const iconData = defaultIconState.find(icon => icon.id === iconState.id);
      if (!iconData) return null;

      // Create complete IconProps object with all required properties
      const iconProps: IconProps = {
        ...iconData,
        isFocus: iconState.isFocus || false,
        displayFocus: state.focusing === FOCUSING.ICON,
        measure: () => {},
        // Add missing required properties
        onMouseDown: onMouseDownIcon,
        onDoubleClick: onDoubleClickIcon,
      };

      return iconProps;
    })
    .filter((icon): icon is IconProps => icon !== null);

  const transformAppForFooter = (app: App): FooterApp => ({
    ...app,
    id: app.id.toString(),
    header: app.header || {
      icon: '',
      title: '',
    },
  });

  return (
    <Container
      ref={ref}
      onMouseUp={onMouseUpDesktop}
      onMouseDown={onMouseDownDesktop}
      powerState={state.powerState}
    >
      <Icons
        icons={icons}
        mouse={mouse}
        selecting={state.selecting}
        setSelectedIcons={onIconsSelected}
        displayFocus={state.focusing === FOCUSING.ICON}
        appSettings={appSettings}
      />
      {state.selecting && (
        <DashedBox startPos={state.selecting} mouse={mouse} />
      )}
      <Windows
        apps={state.apps}
        onMouseDown={onFocusApp}
        onClose={onCloseApp}
        onMinimize={onMinimizeWindow}
        onMaximize={onMaximizeWindow}
        focusedAppId={focusedAppId}
      />
      <Footer
        apps={state.apps.map(transformAppForFooter)}
        onMouseDownApp={onMouseDownFooterApp}
        focusedAppId={focusedAppId.toString()}
        onMouseDown={onMouseDownFooter}
        onClickMenuItem={onClickMenuItem}
      />
      {state.powerState !== POWER_STATE.START && (
        <Modal
          onClose={onModalClose}
          onClickButton={onClickModalButton}
          mode={state.powerState}
        />
      )}
    </Container>
  );
};

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;

const animationMap: Record<POWER_STATE, ReturnType<typeof css>> = {
  [POWER_STATE.START]: css``,
  [POWER_STATE.TURN_OFF]: css`
    ${powerOffAnimation}
  `,
  [POWER_STATE.LOG_OFF]: css`
    ${powerOffAnimation}
  `,
};

const Container = styled.div<{ powerState: POWER_STATE }>`
  font-family: Tahoma, 'Noto Sans', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: url(${backgroundImage}) no-repeat center center fixed;
  background-size: cover;
  animation: ${({ powerState }) => animationMap[powerState]} 5s forwards;
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

export default WinXP;
